import { extractErrorMessage } from "../utils/acceptSuggestions";
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Loader2, CheckCircle2, AlertTriangle, Mail, ShieldCheck, Eye, EyeOff,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AcceptInvitePage
 * ────────────────
 * Public route for new users opening an invitation link
 * (`/accept-invite/{token}`) from email or Slack DM. Verifies the token,
 * lets them set a password, then forwards them to /login.
 *
 * Calls the existing `POST /api/auth/accept-invite/{token}?password=...`
 * backend endpoint. No auth-engine refactor — we just expose a UI for
 * the contract that already exists.
 */
export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);

  // Try to lookup the invitation on mount so we can show the recipient's
  // name + organization in the form. The platform doesn't (yet) expose a
  // public GET endpoint for this, so failure is *non-fatal* — we still
  // render the password form so the POST /accept-invite/{token} contract
  // can complete. A real expired/invalid token surfaces on submit.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/auth/invite/${token}`);
        if (!cancelled) setInvite(r.data);
      } catch {
        // Best-effort metadata only — let user proceed regardless.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      // Backend signature: `password` is a QUERY parameter (existing
      // contract — preserved as-is, no auth-engine refactor).
      await axios.post(
        `${API_URL}/api/auth/accept-invite/${token}`,
        null,
        { params: { password } }
      );
      setSuccess(true);
      toast.success("Account activated. Redirecting to sign-in…");
      setTimeout(() => navigate("/login"), 1800);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Failed to accept invitation"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-50">
        <div className="text-slate-500 flex items-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin" /> Verifying your invitation…
        </div>
      </div>
    );
  }

  // B1 (P65) — a signed-in visitor must not silently accept an invitation meant
  // for someone else. Show a notice + choices; send NO write until they choose.
  // Signing out (existing AuthContext.logout) stays on this route so the normal
  // accept form appears once user is null.
  if (user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-50 p-4">
        <Card className="max-w-md w-full" data-testid="accept-invite-signed-in">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="p-1.5 rounded-lg bg-amber-100">
                <AlertTriangle className="w-4 h-4 text-amber-700" />
              </span>
              You're already signed in
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-700" data-testid="accept-invite-signedin-notice">
              You're signed in as <span className="font-semibold">{user.email}</span>. This
              invitation is for the person it was sent to. Sign out to continue with this invitation.
            </p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => logout()}
                className="w-full bg-indigo-700 hover:bg-indigo-800 text-white"
                data-testid="accept-invite-signout-continue"
              >
                Sign out and continue
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard")}
                className="w-full"
                data-testid="accept-invite-go-dashboard"
              >
                Go to dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-rose-50 p-4">
        <Card className="max-w-md w-full border-rose-200" data-testid="accept-invite-error">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" /> Invitation cannot be used
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-600">{error}</p>
            <Link
              to="/login"
              className="inline-block text-sm font-semibold text-sky-700 hover:text-sky-900"
              data-testid="accept-invite-back-to-login"
            >
              ← Back to sign-in
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-emerald-50 p-4">
        <Card className="max-w-md w-full border-emerald-200" data-testid="accept-invite-success">
          <CardContent className="p-8 text-center space-y-3">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-7 h-7 text-emerald-700" />
            </div>
            <h2 className="text-xl font-bold text-slate-800">Account activated</h2>
            <p className="text-sm text-slate-600">Redirecting you to sign-in…</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-50 p-4">
      <Card className="max-w-md w-full" data-testid="accept-invite-page">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="p-1.5 rounded-lg bg-indigo-100">
              <ShieldCheck className="w-4 h-4 text-indigo-700" />
            </span>
            Activate your ResiliencyHUB.AI account
          </CardTitle>
        </CardHeader>
        <CardContent>
          {invite && (
            <Alert className="mb-4 bg-sky-50 border-sky-200">
              <Mail className="w-4 h-4 text-sky-700" />
              <AlertDescription className="text-xs text-slate-700 ml-1">
                Inviting <span className="font-semibold">{invite.first_name} {invite.last_name}</span>
                {invite.email && <> ({invite.email})</>} as
                <span className="font-semibold"> {(invite.role || "").replace(/_/g, " ")}</span>
                {invite.organization_name && <> for <span className="font-semibold">{invite.organization_name}</span></>}.
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-3" data-testid="accept-invite-form">
            <div>
              <Label htmlFor="pwd" className="text-xs font-semibold text-slate-700">
                Choose a password (min 8 chars)
              </Label>
              <div className="relative">
                <Input
                  id="pwd"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  data-testid="accept-invite-password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-2 top-2 text-slate-400 hover:text-slate-700"
                  tabIndex={-1}
                  aria-label={showPwd ? "Hide password" : "Show password"}
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="confirm" className="text-xs font-semibold text-slate-700">
                Confirm password
              </Label>
              <Input
                id="confirm"
                type={showPwd ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                data-testid="accept-invite-confirm"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-indigo-700 hover:bg-indigo-800 text-white"
              data-testid="accept-invite-submit"
            >
              {submitting
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Activating…</>
                : "Activate account & continue"}
            </Button>
            <p className="text-[0.7rem] text-slate-400 text-center">
              By activating you accept the ResiliencyHUB.AI terms of service.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
